<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'
import type { LocationQuery, LocationQueryRaw } from 'vue-router'

import { useDebounceFn } from '@vueuse/core'
import { computed, reactive, ref, watch } from 'vue'

import type {
  AdminUserDto,
  AdminUserListParams,
  AdminUserTypeFilter,
  AdminUserSortField,
} from '@/types'

import { toAppColor } from '@/composables/useAppColor'
import { appConfirm } from '@/composables/useAppConfirm'
import { appTableServerSortHeader } from '@/composables/useAppTable'
import { useAppToast } from '@/composables/useAppToast'
import { adminService } from '@/services/adminService'
import { formatLocalDateOnly } from '@/utils/dateOnly'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

type BooleanFilterValue = '' | 'true' | 'false'
type AdminUserSortDirection = 'asc' | 'desc'

interface AdminUserListState {
  page: number
  pageSize: number
  sortBy: AdminUserSortField
  sortDirection: AdminUserSortDirection
  search: string
  userType: '' | AdminUserTypeFilter
  emailConfirmed: BooleanFilterValue
  isActive: BooleanFilterValue
  createdAfter: string
  createdBefore: string
}

const DEFAULT_PAGE_SIZE = 20
const DEFAULT_SORT_BY: AdminUserSortField = 'createdAt'
const DEFAULT_SORT_DIRECTION: AdminUserSortDirection = 'desc'
const USER_TYPE_FILTER_VALUES = ['external', 'student', 'partner', 'employee', 'guest'] as const
const SORTABLE_FIELDS: AdminUserSortField[] = [
  'createdAt',
  'lastLogin',
  'userName',
  'email',
  'firstName',
  'lastName',
  'userType',
  'emailConfirmed',
  'isActive',
]

const route = useRoute()
const router = useRouter()
const { t, d } = useI18n()
const { setPageTitle } = useBreadcrumbs()
const toast = useAppToast()
const { userInfo, fetchUserInfo } = useUser()

const users = ref<AdminUserDto[]>([])
const totalCount = ref(0)
const loading = ref(true)
const error = ref<string | null>(null)
const impersonatingUserId = ref<string | null>(null)

const showSeedDialog = ref(false)
const seedCount = ref(10)
const seeding = ref(false)

const state = reactive<AdminUserListState>({
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  sortBy: DEFAULT_SORT_BY,
  sortDirection: DEFAULT_SORT_DIRECTION,
  search: '',
  userType: '',
  emailConfirmed: '',
  isActive: '',
  createdAfter: '',
  createdBefore: '',
})

const rowsPerPageOptions = [10, 20, 50, 100]
const columns = computed<TableColumn<AdminUserDto>[]>(() => {
  const sortable = (field: AdminUserSortField, label: string) =>
    appTableServerSortHeader(label, field, state.sortBy, state.sortDirection, onSortField)

  return [
    {
      accessorKey: 'userName',
      header: sortable('userName', t('pages.admin.users.columns.userName')),
    },
    { accessorKey: 'email', header: sortable('email', t('pages.admin.users.columns.email')) },
    {
      accessorKey: 'firstName',
      header: sortable('firstName', t('pages.admin.users.columns.name')),
    },
    {
      accessorKey: 'userType',
      header: sortable('userType', t('pages.admin.users.columns.userType')),
    },
    {
      accessorKey: 'emailConfirmed',
      header: sortable('emailConfirmed', t('pages.admin.users.columns.emailConfirmed')),
    },
    {
      accessorKey: 'isActive',
      header: sortable('isActive', t('pages.admin.users.columns.status')),
    },
    {
      accessorKey: 'createdAt',
      header: sortable('createdAt', t('pages.admin.users.columns.createdAt')),
    },
    {
      accessorKey: 'lastLogin',
      header: sortable('lastLogin', t('pages.admin.users.columns.lastLogin')),
    },
    { id: 'actions', header: t('common.labels.actions') },
  ]
})

const createdAfterDate = computed({
  get: () => parseDateOnly(state.createdAfter),
  set: (value: Date | null) => {
    state.createdAfter = formatLocalDateOnly(value)
  },
})

const createdBeforeDate = computed({
  get: () => parseDateOnly(state.createdBefore),
  set: (value: Date | null) => {
    state.createdBefore = formatLocalDateOnly(value)
  },
})

const userTypeOptions = computed(() => [
  { label: t('common.labels.all'), value: '' },
  ...USER_TYPE_FILTER_VALUES.map((value) => ({
    label: t(`enums.userType.${value}`),
    value,
  })),
])

const emailConfirmedOptions = computed(() => [
  { label: t('common.labels.all'), value: '' },
  { label: t('pages.admin.users.filters.emailConfirmedYes'), value: 'true' },
  { label: t('pages.admin.users.filters.emailConfirmedNo'), value: 'false' },
])

const activeOptions = computed(() => [
  { label: t('common.labels.all'), value: '' },
  { label: t('common.status.active'), value: 'true' },
  { label: t('common.status.inactive'), value: 'false' },
])

const activeFilterCount = computed(() => {
  return [
    state.search.trim(),
    state.userType,
    state.emailConfirmed,
    state.isActive,
    state.createdAfter,
    state.createdBefore,
  ].filter(Boolean).length
})

const totalRecordsLabel = computed(() =>
  t('pages.admin.users.summary', {
    count: totalCount.value,
  }),
)

const apiParams = computed<AdminUserListParams>(() => ({
  page: state.page,
  pageSize: state.pageSize,
  sortBy: state.sortBy,
  sortDirection: state.sortDirection,
  search: state.search.trim() || undefined,
  userType: state.userType || undefined,
  emailConfirmed: parseBooleanFilter(state.emailConfirmed),
  isActive: parseBooleanFilter(state.isActive),
  createdAfter: toStartOfLocalDayIso(state.createdAfter),
  createdBefore: toEndOfLocalDayIso(state.createdBefore),
}))

const debouncedSearchCommit = useDebounceFn(async () => {
  state.page = 1
  await commitStateToRoute()
}, 350)

function parseDateOnly(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null
  }

  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function toStartOfLocalDayIso(value: string): string | undefined {
  const parsed = parseDateOnly(value)
  if (!parsed) {
    return undefined
  }

  parsed.setHours(0, 0, 0, 0)
  return parsed.toISOString()
}

function toEndOfLocalDayIso(value: string): string | undefined {
  const parsed = parseDateOnly(value)
  if (!parsed) {
    return undefined
  }

  parsed.setHours(23, 59, 59, 999)
  return parsed.toISOString()
}

function parseBooleanFilter(value: BooleanFilterValue): boolean | undefined {
  if (value === 'true') {
    return true
  }

  if (value === 'false') {
    return false
  }

  return undefined
}

function getQueryValue(value: LocationQuery[string]): string {
  if (Array.isArray(value)) {
    return value[0] ?? ''
  }

  return value ?? ''
}

function parsePositiveInt(value: string, fallback: number): number {
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

function parseSortBy(value: string): AdminUserSortField {
  return SORTABLE_FIELDS.includes(value as AdminUserSortField)
    ? (value as AdminUserSortField)
    : DEFAULT_SORT_BY
}

function parseSortDirection(value: string): AdminUserSortDirection {
  return value === 'asc' || value === 'desc' ? value : DEFAULT_SORT_DIRECTION
}

function parseUserTypeFilter(value: string): '' | AdminUserTypeFilter {
  return USER_TYPE_FILTER_VALUES.includes(value as AdminUserTypeFilter)
    ? (value as AdminUserTypeFilter)
    : ''
}

function parseBooleanFilterValue(value: string): BooleanFilterValue {
  return value === 'true' || value === 'false' ? value : ''
}

function parseDateQuery(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ''
}

function applyRouteQuery(query: LocationQuery) {
  state.page = parsePositiveInt(getQueryValue(query.page), 1)
  state.pageSize = parsePositiveInt(getQueryValue(query.pageSize), DEFAULT_PAGE_SIZE)
  state.sortBy = parseSortBy(getQueryValue(query.sortBy))
  state.sortDirection = parseSortDirection(getQueryValue(query.sortDirection))
  state.search = getQueryValue(query.search).trim()
  state.userType = parseUserTypeFilter(getQueryValue(query.userType))
  state.emailConfirmed = parseBooleanFilterValue(getQueryValue(query.emailConfirmed))
  state.isActive = parseBooleanFilterValue(getQueryValue(query.isActive))
  state.createdAfter = parseDateQuery(getQueryValue(query.createdAfter))
  state.createdBefore = parseDateQuery(getQueryValue(query.createdBefore))
}

function buildQueryFromState(): LocationQueryRaw {
  const query: LocationQueryRaw = {}

  if (state.page !== 1) {
    query.page = String(state.page)
  }

  if (state.pageSize !== DEFAULT_PAGE_SIZE) {
    query.pageSize = String(state.pageSize)
  }

  if (state.sortBy !== DEFAULT_SORT_BY) {
    query.sortBy = state.sortBy
  }

  if (state.sortDirection !== DEFAULT_SORT_DIRECTION) {
    query.sortDirection = state.sortDirection
  }

  if (state.search.trim()) {
    query.search = state.search.trim()
  }

  if (state.userType) {
    query.userType = state.userType
  }

  if (state.emailConfirmed) {
    query.emailConfirmed = state.emailConfirmed
  }

  if (state.isActive) {
    query.isActive = state.isActive
  }

  if (state.createdAfter) {
    query.createdAfter = state.createdAfter
  }

  if (state.createdBefore) {
    query.createdBefore = state.createdBefore
  }

  return query
}

function normalizeQueryForComparison(
  query: LocationQuery | LocationQueryRaw,
): Record<string, string> {
  return Object.keys(query)
    .sort()
    .reduce<Record<string, string>>((accumulator, key) => {
      const rawValue = query[key]
      if (rawValue === undefined || rawValue === null) {
        return accumulator
      }

      accumulator[key] = Array.isArray(rawValue) ? String(rawValue[0] ?? '') : String(rawValue)
      return accumulator
    }, {})
}

async function commitStateToRoute() {
  const nextQuery = buildQueryFromState()
  const currentQuery = normalizeQueryForComparison(route.query)
  const normalizedNextQuery = normalizeQueryForComparison(nextQuery)

  if (JSON.stringify(currentQuery) === JSON.stringify(normalizedNextQuery)) {
    await fetchUserList()
    return
  }

  await router.replace({ query: nextQuery })
}

async function fetchUserList() {
  loading.value = true
  error.value = null

  try {
    const response = await adminService.getUserList(apiParams.value)
    users.value = response.items
    totalCount.value = response.totalCount
  } catch (err) {
    console.error('Error fetching user list:', err)
    users.value = []
    totalCount.value = 0
    error.value = t('pages.admin.users.messages.loadError')
  } finally {
    loading.value = false
  }
}

function viewUserDetails(userId: string) {
  router.push(`/admin/users/${userId}`)
}

function isCurrentUser(userId: string) {
  return userInfo.value?.id === userId
}

function formatUserLabel(user: AdminUserDto) {
  return (
    `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
    user.userName ||
    user.email ||
    user.id
  )
}

function formatUserType(userType: string | null | undefined) {
  return userType ? t(`enums.userType.${userType}`) : t('common.labels.unknown')
}

function getUserTypeBadgeClass(userType: string | null | undefined) {
  if (!userType) {
    return 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-100'
  }

  const userTypeLower = userType.toLowerCase()
  if (userTypeLower.includes('external') || userTypeLower.includes('guest')) {
    return 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200'
  }
  if (userTypeLower.includes('student')) {
    return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200'
  }
  if (userTypeLower.includes('employee')) {
    return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
  }
  if (userTypeLower.includes('partner')) {
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
  }

  return 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-100'
}

function formatFullName(user: AdminUserDto) {
  return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || t('common.labels.unknown')
}

function onPage(page: number) {
  state.page = page
  commitStateToRoute()
}

function onPageSize(pageSize: number) {
  state.pageSize = pageSize
  state.page = 1
  commitStateToRoute()
}

function onSortField(field: string) {
  const nextField = parseSortBy(field)
  state.sortDirection = state.sortBy === nextField && state.sortDirection === 'asc' ? 'desc' : 'asc'
  state.sortBy = nextField
  state.page = 1
  commitStateToRoute()
}

function applyFilters() {
  state.page = 1
  commitStateToRoute()
}

function clearFilters() {
  state.search = ''
  state.userType = ''
  state.emailConfirmed = ''
  state.isActive = ''
  state.createdAfter = ''
  state.createdBefore = ''
  state.page = 1
  commitStateToRoute()
}

function openSeedDialog() {
  seedCount.value = 10
  showSeedDialog.value = true
}

async function createFakeUsers() {
  seeding.value = true
  try {
    const result = await adminService.seedFakeUsers(seedCount.value)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.admin.users.messages.seedSuccess', { count: result.created }),
      duration: 4000,
    })
    showSeedDialog.value = false
    await fetchUserList()
  } catch (err) {
    console.error('Error creating fake users:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.admin.users.messages.seedError'),
      duration: 4000,
    })
  } finally {
    seeding.value = false
  }
}

async function confirmImpersonateUser(user: AdminUserDto) {
  if (impersonatingUserId.value || isCurrentUser(user.id)) {
    return
  }

  const userLabel = formatUserLabel(user)

  const accepted = await appConfirm({
    title: t('pages.admin.users.impersonation.header'),
    message: t('pages.admin.users.impersonation.message', { user: userLabel }),
    confirmLabel: t('pages.admin.users.impersonation.accept'),
    cancelLabel: t('common.actions.cancel'),
    confirmColor: 'warning',
  })
  if (!accepted) return

  impersonatingUserId.value = user.id
  try {
    await adminService.impersonateUser(user.id)
    await fetchUserInfo(true)
    toast.add({
      color: 'success',
      title: t('pages.admin.users.impersonation.successSummary'),
      description: t('pages.admin.users.impersonation.successDetail', { user: userLabel }),
      duration: 4000,
    })
    await router.push('/')
  } catch (err) {
    console.error('Error impersonating user:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.admin.users.impersonation.error'),
      duration: 5000,
    })
  } finally {
    impersonatingUserId.value = null
  }
}

watch(
  () => route.query,
  async (query) => {
    applyRouteQuery(query)
    await fetchUserList()
  },
  { immediate: true },
)

onMounted(() => {
  setPageTitle(t('pages.admin.users.title'))
})
</script>

<template>
  <div class="space-y-6">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold">{{ t('pages.admin.users.title') }}</h1>
        <p class="text-muted">
          {{ t('pages.admin.users.subtitle') }}
        </p>
      </div>

      <div class="flex flex-wrap gap-2">
        <UButton
          icon="i-lucide-users"
          :label="t('pages.admin.users.actions.createTestUsers')"
          color="neutral"
          @click="openSeedDialog"
        />
        <UButton
          icon="i-lucide-funnel-x"
          :disabled="activeFilterCount === 0"
          :label="t('pages.admin.users.actions.clearFilters')"
          @click="clearFilters"
          variant="outline"
        />
        <UButton
          icon="i-lucide-refresh-cw"
          :loading="loading"
          @click="fetchUserList"
          variant="outline"
        />
      </div>
    </div>

    <AppAlert v-if="error" color="error" :closable="false">
      {{ error }}
    </AppAlert>

    <UCard>
      <template #default>
        <div class="space-y-4">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
            <div class="xl:col-span-2">
              <label class="mb-2 block text-sm font-medium">
                {{ t('pages.admin.users.filters.search') }}
              </label>
              <AppIconField fluid>
                <AppInputIcon name="i-lucide-search" />
                <UInput
                  v-model="state.search"
                  :placeholder="t('pages.admin.users.filters.searchPlaceholder')"
                  @update:model-value="debouncedSearchCommit"
                  class="w-full"
                />
              </AppIconField>
            </div>

            <div>
              <label class="mb-2 block text-sm font-medium">
                {{ t('pages.admin.users.filters.userType') }}
              </label>
              <AppSelect
                v-model="state.userType"
                fluid
                :options="userTypeOptions"
                option-label="label"
                option-value="value"
                @update:model-value="applyFilters"
              />
            </div>

            <div>
              <label class="mb-2 block text-sm font-medium">
                {{ t('pages.admin.users.filters.emailConfirmed') }}
              </label>
              <AppSelect
                v-model="state.emailConfirmed"
                fluid
                :options="emailConfirmedOptions"
                option-label="label"
                option-value="value"
                @update:model-value="applyFilters"
              />
            </div>

            <div>
              <label class="mb-2 block text-sm font-medium">
                {{ t('pages.admin.users.filters.status') }}
              </label>
              <AppSelect
                v-model="state.isActive"
                fluid
                :options="activeOptions"
                option-label="label"
                option-value="value"
                @update:model-value="applyFilters"
              />
            </div>

            <div>
              <label class="mb-2 block text-sm font-medium">
                {{ t('pages.admin.users.filters.createdAfter') }}
              </label>
              <AppDatePicker
                v-model="createdAfterDate"
                fluid
                show-icon
                icon-display="input"
                @update:model-value="applyFilters"
              />
            </div>

            <div>
              <label class="mb-2 block text-sm font-medium">
                {{ t('pages.admin.users.filters.createdBefore') }}
              </label>
              <AppDatePicker
                v-model="createdBeforeDate"
                fluid
                show-icon
                icon-display="input"
                @update:model-value="applyFilters"
              />
            </div>
          </div>

          <div class="flex flex-wrap items-center justify-between gap-3 text-sm text-muted">
            <span>{{ totalRecordsLabel }}</span>
            <span v-if="activeFilterCount > 0">
              {{ t('pages.admin.users.activeFilters', { count: activeFilterCount }) }}
            </span>
          </div>

          <AppDataTableShell :data="users" :columns="columns" :loading="loading">
            <template #empty>
              {{ t('pages.admin.users.empty') }}
            </template>

            <template #userName-cell="{ row }">
              <router-link
                :to="`/admin/users/${row.original.id}`"
                class="text-[var(--app-text-primary)] hover:underline"
              >
                {{ row.original.userName || row.original.email || row.original.id }}
              </router-link>
            </template>

            <template #firstName-cell="{ row }">{{ formatFullName(row.original) }}</template>

            <template #userType-cell="{ row }">
              <span
                class="inline-flex rounded px-2 py-1 text-xs font-medium"
                :class="getUserTypeBadgeClass(row.original.userType)"
              >
                {{ formatUserType(row.original.userType) }}
              </span>
            </template>

            <template #emailConfirmed-cell="{ row }">
              <UBadge
                :label="
                  row.original.emailConfirmed
                    ? t('pages.admin.users.status.emailConfirmed')
                    : t('pages.admin.users.status.emailUnconfirmed')
                "
                :color="toAppColor(row.original.emailConfirmed ? 'success' : 'warn')"
              />
            </template>

            <template #isActive-cell="{ row }">
              <UBadge
                :label="
                  row.original.isActive ? t('common.status.active') : t('common.status.inactive')
                "
                :color="toAppColor(row.original.isActive ? 'success' : 'danger')"
              />
            </template>

            <template #createdAt-cell="{ row }">
              <span v-if="row.original.createdAt">
                {{ d(new Date(row.original.createdAt), 'datetime') }}
              </span>
              <span v-else class="text-dimmed">
                {{ t('common.labels.unknown') }}
              </span>
            </template>

            <template #lastLogin-cell="{ row }">
              <span v-if="row.original.lastLogin">
                {{ d(new Date(row.original.lastLogin), 'datetime') }}
              </span>
              <span v-else class="text-dimmed">
                {{ t('pages.admin.users.status.never') }}
              </span>
            </template>

            <template #actions-cell="{ row }">
              <div class="flex items-center gap-1">
                <UButton
                  icon="i-lucide-user-pen"
                  size="sm"
                  :aria-label="t('pages.admin.users.actions.editUser')"
                  @click="viewUserDetails(row.original.id)"
                  variant="ghost"
                />
                <UButton
                  v-if="!isCurrentUser(row.original.id)"
                  icon="i-lucide-user-plus"
                  size="sm"
                  color="warning"
                  :aria-label="t('pages.admin.users.actions.impersonateUser')"
                  :loading="impersonatingUserId === row.original.id"
                  @click="confirmImpersonateUser(row.original)"
                  variant="ghost"
                />
              </div>
            </template>
          </AppDataTableShell>
          <div class="mt-3 flex flex-wrap items-center justify-between gap-3">
            <USelect
              :model-value="state.pageSize"
              :items="rowsPerPageOptions"
              class="w-24"
              @update:model-value="onPageSize"
            />
            <UPagination
              :page="state.page"
              :total="totalCount"
              :items-per-page="state.pageSize"
              @update:page="onPage"
            />
          </div>
        </div>
      </template>
    </UCard>

    <AppModal
      v-model:open="showSeedDialog"
      :title="t('pages.admin.users.seedDialog.title')"
      modal
      :style="{ width: '26rem' }"
    >
      <div class="flex flex-col gap-4">
        <p class="text-sm text-muted">
          {{ t('pages.admin.users.seedDialog.description') }}
        </p>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium" for="seed-count">
            {{ t('pages.admin.users.seedDialog.countLabel') }}
          </label>
          <UInputNumber id="seed-count" v-model="seedCount" :min="1" :max="200" />
        </div>
      </div>

      <template #footer>
        <UButton
          :label="t('common.actions.cancel')"
          color="neutral"
          @click="showSeedDialog = false"
        />
        <UButton
          :label="t('common.actions.create')"
          icon="i-lucide-user-plus"
          :loading="seeding"
          @click="createFakeUsers"
        />
      </template>
    </AppModal>
  </div>
</template>
